/**
 * Create / edit a connector group (VSNP-1097).
 *
 * The group NAME is the join key apps and connectors reference, so a rename is
 * only accepted server-side when the group is unreferenced; the API returns a
 * clear message otherwise, surfaced here as a toast. The inner form is a keyed
 * child so it remounts with fresh state on each open (no setState-in-effect,
 * which this repo's lint bans).
 */

import { useState } from 'react';
import Modal from '../common/Modal';
import AccessFormField from './AccessFormField';
import { useToast } from '../common/ToastContainer';
import accessService, { type ConnectorGroupSummary } from '../../services/access.service';

const NAME_RE = /^[a-zA-Z0-9][a-zA-Z0-9._-]*$/;

interface ConnectorGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  /** null = create; a registered group = edit. */
  group: ConnectorGroupSummary | null;
}

const ConnectorGroupModal = ({ isOpen, onClose, onSaved, group }: ConnectorGroupModalProps) => (
  <Modal isOpen={isOpen} onClose={onClose} title={group ? 'Edit connector group' : 'New connector group'} size="md">
    {isOpen && (
      <GroupForm key={group?.id ?? 'new'} group={group} onClose={onClose} onSaved={onSaved} />
    )}
  </Modal>
);

const GroupForm = ({
  group,
  onClose,
  onSaved,
}: {
  group: ConnectorGroupSummary | null;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const toast = useToast();
  const isEdit = !!group?.id;
  const [name, setName] = useState(group?.connectorGroup ?? '');
  const [description, setDescription] = useState(group?.description ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const trimmed = name.trim();
    if (!NAME_RE.test(trimmed)) {
      setError('Name may contain letters, digits, dot, dash, underscore (no spaces).');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      if (isEdit) {
        await accessService.updateConnectorGroup(group!.id!, { name: trimmed, description: description.trim() || null });
        toast.success('Group updated', trimmed);
      } else {
        await accessService.createConnectorGroup({ name: trimmed, description: description.trim() || undefined });
        toast.success('Group created', trimmed);
      }
      onSaved();
      onClose();
    } catch (err) {
      // The API refuses a rename with members and duplicate names with a clear
      // message — show it inline rather than only as a toast.
      setError(err instanceof Error ? err.message : 'Could not save the group');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-md border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">{error}</div>
      )}

      <AccessFormField
        label="Name"
        htmlFor="grp-name"
        required
        hint={isEdit ? 'Renaming is only allowed while the group has no connectors or apps.' : 'Apps and connectors reference the group by this name.'}
      >
        <input id="grp-name" className="input-base w-full" placeholder="dc-syd" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </AccessFormField>

      <AccessFormField label="Description" htmlFor="grp-desc">
        <input id="grp-desc" className="input-base w-full" placeholder="Sydney data-centre connectors" value={description} onChange={(e) => setDescription(e.target.value)} />
      </AccessFormField>

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="btn-primary" onClick={submit} disabled={saving || !name.trim()}>
          {saving ? 'Saving…' : isEdit ? 'Save group' : 'Create group'}
        </button>
      </div>
    </div>
  );
};

export default ConnectorGroupModal;
